import type { Circuit, CircuitDecor3dKind, CircuitFreeDecor } from '../types/track.js';

/**
 * Décor en placement libre (éditeur 3D) — voir `CircuitFreeDecor`. Contrairement
 * à `circuitPropEdit.ts`/`circuitOccupancy.ts`, aucune fonction ici ne vérifie
 * d'occupation de case : plusieurs décors libres peuvent se superposer à volonté.
 */

/** Pas de rotation appliqué par le bouton "Tourner" en placement libre (degrés). */
export const FREE_DECOR_ROTATION_STEP_DEG = 15;

function newFreeDecorId(): string {
  return `fd-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeRotationDeg(rotationDeg: number): number {
  const wrapped = rotationDeg % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

export interface NewFreeDecor {
  kind: CircuitDecor3dKind;
  modelId: string;
  x: number;
  y: number;
  rotationDeg: number;
  text?: string;
}

export function addFreeDecor(circuit: Circuit, entry: NewFreeDecor): Circuit {
  const decor: CircuitFreeDecor = {
    id: newFreeDecorId(),
    kind: entry.kind,
    modelId: entry.modelId,
    x: entry.x,
    y: entry.y,
    rotationDeg: normalizeRotationDeg(entry.rotationDeg),
    ...(entry.text ? { text: entry.text } : {}),
  };
  return {
    ...circuit,
    freeDecor: [...(circuit.freeDecor ?? []), decor],
  };
}

export function moveFreeDecor(circuit: Circuit, id: string, x: number, y: number): Circuit {
  const freeDecor = circuit.freeDecor;
  if (!freeDecor?.length) return circuit;
  return {
    ...circuit,
    freeDecor: freeDecor.map((decor) => (decor.id === id ? { ...decor, x, y } : decor)),
  };
}

export function rotateFreeDecor(circuit: Circuit, id: string, rotationDeg: number): Circuit {
  const freeDecor = circuit.freeDecor;
  if (!freeDecor?.length) return circuit;
  const normalized = normalizeRotationDeg(rotationDeg);
  return {
    ...circuit,
    freeDecor: freeDecor.map((decor) =>
      decor.id === id ? { ...decor, rotationDeg: normalized } : decor
    ),
  };
}

/** Bornes larges plutôt qu'une vraie limite métier — évitent surtout qu'un glisser
 * anormalement long (ex. molette système emballée) n'envoie le décor à l'infini. */
export const FREE_DECOR_MIN_ELEVATION = -5;
export const FREE_DECOR_MAX_ELEVATION = 20;

export function setFreeDecorElevation(circuit: Circuit, id: string, elevation: number): Circuit {
  const freeDecor = circuit.freeDecor;
  if (!freeDecor?.length) return circuit;
  const clamped = Math.min(FREE_DECOR_MAX_ELEVATION, Math.max(FREE_DECOR_MIN_ELEVATION, elevation));
  return {
    ...circuit,
    freeDecor: freeDecor.map((decor) =>
      decor.id === id ? { ...decor, elevation: clamped } : decor
    ),
  };
}

export function removeFreeDecor(circuit: Circuit, id: string): Circuit {
  const freeDecor = (circuit.freeDecor ?? []).filter((decor) => decor.id !== id);
  return {
    ...circuit,
    freeDecor: freeDecor.length > 0 ? freeDecor : undefined,
  };
}

/** Décor libre le plus proche de `(x, y)` dans un rayon donné (unités de case), ou `null`. */
export function findFreeDecorNear(
  circuit: Circuit,
  x: number,
  y: number,
  radius: number
): CircuitFreeDecor | null {
  let closest: CircuitFreeDecor | null = null;
  let closestDist = radius;
  for (const decor of circuit.freeDecor ?? []) {
    const dist = Math.hypot(decor.x - x, decor.y - y);
    if (dist <= closestDist) {
      closest = decor;
      closestDist = dist;
    }
  }
  return closest;
}
